/**
 * Project-related utility functions
 */

/**
 * Extracts unique customer IDs from a project structure.
 * Returns the root CustomerID first, followed by all unique EventClientIDs
 * from the Events array (if present and different from root).
 *
 * @param {Object} project - The project object
 * @returns {Array<number>} Array of unique customer IDs
 *
 * @example
 * const project = {
 *   CustomerID: 10174,
 *   Events: [
 *     { EventClientID: 4279 },
 *     { EventClientID: null },
 *     { EventClientID: 4279 } // duplicate, will be filtered
 *   ]
 * };
 * getProjectCustomerIds(project); // Returns: [10174, 4279]
 */
const getProjectCustomerIds = (project) => {
    if (!project) return [];

    const customerIds = [];

    // Add root CustomerID first
    if (project.CustomerID) {
        customerIds.push(project.CustomerID);
    }

    // Add unique EventClientIDs that are present and different
    if (project.Events && Array.isArray(project.Events)) {
        project.Events.forEach((event) => {
            if (
                event.EventClientID &&
                event.EventClientID !== project.CustomerID &&
                !customerIds.includes(event.EventClientID)
            ) {
                customerIds.push(event.EventClientID);
            }
        });
    }

    return customerIds;
};

const getProjectTitles = (project) => {
    const titles = [project.Name];
    if (project.Product && project.Product.toLowerCase() !== project.Name?.toLowerCase()) {
        titles.push(project.Product);
    }
    return titles;
};

const getProjectEventsTitles = (project) => {
    const titles = [];
    const seenEventNames = new Set();
    project.Events.forEach((event) => {
        if (event.Name && !seenEventNames.has(event.Name) && event.Name.toLowerCase() !== project.Name?.toLowerCase()) {
            titles.push(event.Name);
            seenEventNames.add(event.Name);
        }
    });
    return titles;
};

const getProjectTitlesOptions = (project) => {
    const titles = getProjectTitles(project);
    const eventTitles = getProjectEventsTitles(project);
    const titleOptions = [];
    if (titles.length > 0) {
        titleOptions.push({
            label: 'Project',
            options: titles.map((title) => ({ label: title, value: title })),
        });
    }
    if (eventTitles.length > 0) {
        titleOptions.push({
            label: 'Event',
            options: eventTitles.map((title) => ({ label: title, value: title })),
        });
    }
    return titleOptions;
};

const getProjectDescriptions = (project) => {
    const descriptions = [];
    if (project.Description) {
        descriptions.push(project.Description);
    }
    if (project.AccountingNote && project.AccountingNote.toLowerCase() !== project.Description?.toLowerCase()) {
        descriptions.push(project.AccountingNote);
    }
    return descriptions;
};

const getProjectEventDescriptions = (project) => {
    const descriptions = [];
    const seenEventDesc = new Set();
    project.Events.forEach((event) => {
        if (
            event.Description &&
            !seenEventDesc.has(event.Description) &&
            event.Description.toLowerCase() !== project.Description?.toLowerCase()
        ) {
            descriptions.push(event.Description);
            seenEventDesc.add(event.Description);
        }
    });
    return descriptions;
};

const getProjectEventInternalNotes = (project) => {
    const internalNotes = [];
    const seenEventInternalNote = new Set();
    project.Events.forEach((event) => {
        if (
            event.InternalNote &&
            !seenEventInternalNote.has(event.InternalNote) &&
            event.InternalNote.toLowerCase() !== project.InternalNote?.toLowerCase()
        ) {
            internalNotes.push(event.InternalNote);
            seenEventInternalNote.add(event.InternalNote);
        }
    });
    return internalNotes;
};

const getProjectDescriptionsOptions = (project) => {
    const descriptions = getProjectDescriptions(project);
    const eventDescriptions = getProjectEventDescriptions(project);
    const descriptionOptions = [];
    if (eventDescriptions.length > 0) {
        descriptionOptions.push({
            label: 'Events',
            options: eventDescriptions.map((description) => ({ label: description, value: description })),
        });
    }
    if (descriptions.length > 0) {
        descriptionOptions.push({
            label: 'Project',
            options: descriptions.map((description) => ({ label: description, value: description })),
        });
    }
    return descriptionOptions;
};

const getProjectInternalNotesOptions = (project) => {
    const internalNotes = getProjectEventInternalNotes(project);
    const internalNoteOptions = [];
    if (internalNotes.length > 0) {
        internalNoteOptions.push({
            label: 'Events',
            options: internalNotes.map((internalNote) => ({ label: internalNote, value: internalNote })),
        });
    }
    return internalNoteOptions;
};

export {
    getProjectCustomerIds,
    getProjectTitlesOptions,
    getProjectDescriptionsOptions,
    getProjectInternalNotesOptions,
};
